import {
  Box,
  ClickAwayListener,
  Collapse,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { SortAscIcon, SortDescIcon } from 'components/SVG';
import { useSparkRunsStatusTypes } from 'hooks/EntityTypes/useSparkRunsStatusTypes';
import React from 'react';
import { HistoryFilter } from '.';
import { showRunsSparkStatus } from 'components/Dashboard/HelperFunctions/showRecentSparkStatuses';
import { DropdownOption } from 'types/common/DropdownOption';
import { useDialog } from 'hooks/common/useDialog';
import type { SparkRunsStatusType } from 'types/entityTypes/SparkRunsStatusTypes';
import { SparkImageLoader } from 'sharedComponents/UI';
import SparkRunsHistory from 'components/sparkRunHistory/SparkRunsHistory';

type StatusSelectorProps = {
  statusValue: string | number;
  statusAttributes: SparkRunsStatusType['attributes'] | undefined;
};

const StatusSelector = ({
  statusValue,
  statusAttributes,
}: StatusSelectorProps) => {
  return (
    <Box display='flex' alignItems='center' padding={1}>
      {statusValue === 'all' ? (
        showRunsSparkStatus('all')
      ) : (
        <Box
          display='flex'
          alignItems='center'
          gap={2}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              alignItems: 'flex-start',
              flexDirection: 'column',
              gap: 1,
            },
            [theme.breakpoints.up('sm')]: {
              flexDirection: 'row',
            },
          })}
        >
          <Box marginTop={0.8}>
            <SparkImageLoader
              width='28px'
              height='28px'
              src={statusAttributes?.iconURL}
              alt=''
            />
          </Box>
          <Typography
            sx={{ fontSize: '18px', paddingLeft: '15px' }}
            color={statusAttributes?.textColour}
          >
            {statusAttributes?.name}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

type SparkRunsTabProps = {
  historyFilter: HistoryFilter;
};
const StatusSelectDropDown = ({
  open,
  statusDropDownOptions,
  sparkRunsStatusIdMap,
  onStatusSelect,
}: {
  open: boolean;
  statusDropDownOptions: DropdownOption[];
  sparkRunsStatusIdMap: Record<string, SparkRunsStatusType> | undefined;
  onStatusSelect: (statusID: string) => void;
}) => {
  return (
    <Collapse
      in={open}
      sx={{
        position: 'absolute',
        top: 65,
        left: 0,
        zIndex: 2,
        width: '100%',
        height: '100%',
      }}
    >
      <Paper elevation={2}>
        <Grid container borderRadius='10px' padding={2}>
          {statusDropDownOptions.map((statusDropDownOption, index) => (
            <Grid
              item
              container
              alignItems='center'
              xs={12}
              onClick={() =>
                onStatusSelect(statusDropDownOption.value as string)
              }
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: (theme) => theme.palette.primary.light },
              }}
            >
              {index !== 0 && (
                <Box width='100%'>
                  <Divider />
                </Box>
              )}
              <Box display='flex' alignItems='center' padding={1}>
                <StatusSelector
                  statusValue={statusDropDownOption.value}
                  statusAttributes={
                    sparkRunsStatusIdMap?.[statusDropDownOption.value]
                      ?.attributes
                  }
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Collapse>
  );
};

function SparkRunsTab({ historyFilter }: SparkRunsTabProps) {
  const [selectedRunStatusID, setSelectedRunStatusID] =
    React.useState<string>('all');
  const { open, openDialog, closeDialog } = useDialog();
  const { dropdownOptions, idMap } = useSparkRunsStatusTypes();

  const statusDropDownOptions = [
    { label: 'all', value: 'all' },
    ...dropdownOptions,
  ];
  const toggleOpen = () => {
    if (open) {
      closeDialog();
    } else {
      openDialog();
    }
  };

  const handleStatusSelect = (statusID: string) => {
    setSelectedRunStatusID(statusID);
    closeDialog();
  };
  return (
    <Grid container width='100%'>
      <ClickAwayListener onClickAway={closeDialog}>
        <Box
          width='100%'
          minHeight='60px'
          display='flex'
          alignItems='center'
          position='relative'
          pl={4}
          mb={2}
          onClick={toggleOpen}
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #EEE',
            borderRadius: '10px',
            fontSize: '18px',
            paddingLeft: '15px',
            cursor: 'pointer',
          }}
        >
          <Box width='100%' display='flex'>
            <StatusSelector
              statusValue={selectedRunStatusID}
              statusAttributes={idMap?.[selectedRunStatusID]?.attributes}
            />
          </Box>
          <Box justifySelf='flex-end' marginRight={2}>
            {!open ? <SortAscIcon /> : <SortDescIcon />}
          </Box>
          <StatusSelectDropDown
            open={open}
            statusDropDownOptions={statusDropDownOptions}
            sparkRunsStatusIdMap={idMap}
            onStatusSelect={handleStatusSelect}
          />
        </Box>
      </ClickAwayListener>
      <SparkRunsHistory
        selectedStatus={selectedRunStatusID}
        historyFilter={historyFilter}
        paginatedQuery={{ pageNumber: 1, pageSize: 10 }}
        showPaginationControls={true}
      />
    </Grid>
  );
}

export default SparkRunsTab;
